import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Check, Eye, EyeOff, FileText, Globe2, Phone, ShieldCheck, Trash2, UserRound } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store';
import { authAPI } from '../../services/api';
import AuthShell from '../../components/auth/AuthShell';

const COMPANY_SIZE_OPTIONS = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];
const COMPANY_TYPE_OPTIONS = ['startup', 'corporation', 'nonprofit', 'government', 'agency', 'other'];
const INDUSTRY_OPTIONS = [
  'technology',
  'healthcare',
  'finance',
  'education',
  'manufacturing',
  'retail',
  'consulting',
  'media',
  'real estate',
  'other'
];

const inputBase =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100';

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  companySize: '',
  industry: '',
  companyDescription: '',
  websiteUrl: '',
  headquartersLocation: '',
  companyType: '',
  countryCode: '',
  phoneDialCode: ''
};

const formatOptionLabel = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const normalizePhone = (phone, dialingCode) => {
  const rawPhone = String(phone || '').trim();
  if (!rawPhone) return '';

  if (rawPhone.startsWith('+')) {
    return `+${rawPhone.replace(/\D/g, '')}`;
  }

  const localNumber = rawPhone.replace(/\D/g, '').replace(/^0+/, '');
  const prefix = String(dialingCode || '').replace(/\D/g, '');
  return prefix && localNumber ? `+${prefix}${localNumber}` : '';
};

function Field({ label, error, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700">{label}</label>
      {children}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function RegisterPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [showPasswords, setShowPasswords] = useState({ password: false, confirmPassword: false });
  const [registrationOptions, setRegistrationOptions] = useState({ countries: [], phoneCountries: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState('');
  const [companyProfileDocument, setCompanyProfileDocument] = useState(null);
  const { register, loading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadRegistrationOptions = async () => {
      try {
        setOptionsLoading(true);
        setOptionsError('');
        const options = await authAPI.getEmployerRegistrationOptions();
        if (!cancelled) {
          setRegistrationOptions({
            countries: options.countries || [],
            phoneCountries: options.phoneCountries || []
          });
        }
      } catch (error) {
        if (!cancelled) {
          setOptionsError(error?.message || 'Unable to load supported countries.');
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };

    loadRegistrationOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCountry = useMemo(
    () => registrationOptions.countries.find((country) => country.code === formData.countryCode),
    [formData.countryCode, registrationOptions.countries]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    if (errors[name]) {
      setErrors((previous) => ({ ...previous, [name]: '' }));
    }
  };

  const handleCountryChange = (event) => {
    const countryCode = event.target.value;
    const country = registrationOptions.countries.find((option) => option.code === countryCode);
    setFormData((previous) => ({
      ...previous,
      countryCode,
      phoneDialCode: previous.phoneDialCode || country?.dialingCode || ''
    }));
    setErrors((previous) => ({ ...previous, countryCode: '' }));
  };

  const handleCompanyProfileDocument = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(extension)) {
      toast.error('Upload a PDF, DOC, or DOCX company profile.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Company profile document must be 10MB or smaller.');
      return;
    }
    setCompanyProfileDocument(file);
  };

  const validateCompanyStep = () => {
    const nextErrors = {};

    if (!formData.companyName.trim()) nextErrors.companyName = 'Company name is required.';
    if (!formData.companySize) nextErrors.companySize = 'Company size is required.';
    if (!formData.industry) nextErrors.industry = 'Industry is required.';
    if (!formData.companyType) nextErrors.companyType = 'Company type is required.';
    if (!formData.countryCode) nextErrors.countryCode = 'Company country is required for verification.';
    if (formData.websiteUrl && !/^https?:\/\/.+\..+/.test(formData.websiteUrl)) {
      nextErrors.websiteUrl = 'Use a valid URL such as https://example.com.';
    }

    return nextErrors;
  };

  const validateUserStep = () => {
    const nextErrors = {};
    const normalizedPhone = normalizePhone(formData.phone, formData.phoneDialCode);

    if (!formData.firstName.trim()) nextErrors.firstName = 'First name is required.';
    if (!formData.lastName.trim()) nextErrors.lastName = 'Last name is required.';
    if (!formData.email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (!formData.password) {
      nextErrors.password = 'Password is required.';
    } else if (formData.password.length < 6) {
      nextErrors.password = 'Password must be at least 6 characters.';
    }
    if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match.';
    }
    if (!formData.phone.trim()) {
      nextErrors.phone = 'Phone number is required for WhatsApp verification.';
    } else if (!/^\+[1-9]\d{6,14}$/.test(normalizedPhone)) {
      nextErrors.phone = 'Choose a country code and enter a valid phone number.';
    }

    return nextErrors;
  };

  const handleNextStep = (event) => {
    event.preventDefault();
    const validationErrors = validateCompanyStep();

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      toast.error('Please review the company details.');
      return;
    }

    setErrors({});
    setCurrentStep(2);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateUserStep();

    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      toast.error('Please review your account details.');
      return;
    }

    const apiData = {
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      role: 'employer',
      companyName: formData.companyName.trim(),
      companySize: formData.companySize,
      industry: formData.industry,
      companyType: formData.companyType,
      countryCode: formData.countryCode
    };

    const normalizedPhone = normalizePhone(formData.phone, formData.phoneDialCode);
    if (normalizedPhone) apiData.phone = normalizedPhone;
    if (formData.companyDescription.trim()) apiData.companyDescription = formData.companyDescription.trim();
    if (formData.websiteUrl.trim()) apiData.websiteUrl = formData.websiteUrl.trim();
    if (formData.headquartersLocation.trim()) apiData.headquartersLocation = formData.headquartersLocation.trim();
    if (companyProfileDocument) apiData.companyProfileDocument = companyProfileDocument;

    const result = await register(apiData);

    if (!result.success) {
      setErrors({ general: result.error });
      toast.error(result.error || 'Registration failed.');
      return;
    }

    toast.success('Employer account created.');
    navigate('/contact-verification', { replace: true });
  };

  const renderStepIndicator = () => (
    <div className="mb-8 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
      {[
        { step: 1, label: 'Company', icon: Building2 },
        { step: 2, label: 'Account', icon: UserRound }
      ].map((item, index) => {
        const Icon = item.icon;
        const isActive = currentStep >= item.step;

        return (
          <React.Fragment key={item.step}>
            {index === 1 && <div className="h-px bg-slate-200" />}
            <div className="flex items-center gap-3">
              <span className={`flex h-9 w-9 items-center justify-center rounded-md border text-sm ${
                isActive
                  ? 'border-sky-600 bg-sky-600 text-white'
                  : 'border-slate-200 bg-white text-slate-400'
              }`}>
                {currentStep > item.step ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
              </span>
              <span className={`text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500'}`}>
                {item.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <AuthShell
      title="Create employer account"
      subtitle="Set up your company workspace and primary admin profile."
      maxWidth="max-w-3xl"
    >
      {renderStepIndicator()}

      {errors.general && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {errors.general}
        </div>
      )}

      {currentStep === 1 ? (
        <form onSubmit={handleNextStep} className="space-y-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="Company name" error={errors.companyName}>
              <input
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className={inputBase}
                placeholder="Company Ltd"
                required
              />
            </Field>

            <Field label="Company size" error={errors.companySize}>
              <select name="companySize" value={formData.companySize} onChange={handleChange} className={inputBase} required>
                <option value="">Select size</option>
                {COMPANY_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size} employees</option>
                ))}
              </select>
            </Field>

            <Field label="Industry" error={errors.industry}>
              <select name="industry" value={formData.industry} onChange={handleChange} className={inputBase} required>
                <option value="">Select industry</option>
                {INDUSTRY_OPTIONS.map((industry) => (
                  <option key={industry} value={industry}>{formatOptionLabel(industry)}</option>
                ))}
              </select>
            </Field>

            <Field label="Company type" error={errors.companyType}>
              <select name="companyType" value={formData.companyType} onChange={handleChange} className={inputBase} required>
                <option value="">Select type</option>
                {COMPANY_TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>{formatOptionLabel(type)}</option>
                ))}
              </select>
            </Field>

            <Field label="Company country" error={errors.countryCode}>
              <div className="relative">
                <Globe2 className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleCountryChange}
                  className={`${inputBase} pl-10`}
                  disabled={optionsLoading}
                  required
                >
                  <option value="">{optionsLoading ? 'Loading countries...' : 'Select company country'}</option>
                  {registrationOptions.countries.map((country) => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                  ))}
                </select>
              </div>
            </Field>

            <Field label="Verification region">
              <div className="flex min-h-[46px] items-center gap-3 rounded-md border border-sky-100 bg-sky-50 px-3 py-2.5 text-sm text-sky-800">
                <ShieldCheck className="h-4 w-4 shrink-0 text-sky-600" />
                <span>{selectedCountry?.kycRegionName || 'Select a country to assign the KYC region'}</span>
              </div>
            </Field>

            <Field label="Website (optional)" error={errors.websiteUrl}>
              <input
                name="websiteUrl"
                type="url"
                value={formData.websiteUrl}
                onChange={handleChange}
                className={inputBase}
                placeholder="https://company.com"
              />
            </Field>

            <Field label="Headquarters city or address (optional)">
              <input
                name="headquartersLocation"
                value={formData.headquartersLocation}
                onChange={handleChange}
                className={inputBase}
                placeholder="City, state or province"
              />
            </Field>
          </div>

          {optionsError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {optionsError} Refresh the page to try again.
            </div>
          )}

          <Field label="Company description (optional)">
            <textarea
              name="companyDescription"
              value={formData.companyDescription}
              onChange={handleChange}
              rows={4}
              className={`${inputBase} resize-none`}
              placeholder="A short company overview"
            />
          </Field>

          <Field label="Company profile document (optional)">
            <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4">
              {companyProfileDocument ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText className="h-5 w-5 shrink-0 text-sky-600" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{companyProfileDocument.name}</p>
                      <p className="text-xs text-slate-500">{Math.ceil(companyProfileDocument.size / 1024)} KB</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCompanyProfileDocument(null)}
                    className="rounded-md p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-600"
                    aria-label="Remove company profile document"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex cursor-pointer items-center gap-3">
                  <FileText className="h-5 w-5 shrink-0 text-sky-600" />
                  <span>
                    <span className="block text-sm font-medium text-slate-800">Upload PDF, DOC, or DOCX</span>
                    <span className="block text-xs text-slate-500">
                      We will securely analyze it in the background and suggest profile details for your review.
                    </span>
                  </span>
                  <input
                    type="file"
                    className="sr-only"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleCompanyProfileDocument}
                  />
                </label>
              )}
            </div>
          </Field>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
            >
              Continue
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <Field label="First name" error={errors.firstName}>
              <input name="firstName" value={formData.firstName} onChange={handleChange} className={inputBase} required />
            </Field>
            <Field label="Last name" error={errors.lastName}>
              <input name="lastName" value={formData.lastName} onChange={handleChange} className={inputBase} required />
            </Field>
            <Field label="Work email" error={errors.email}>
              <input name="email" type="email" value={formData.email} onChange={handleChange} className={inputBase} required />
            </Field>
            <Field label="Phone" error={errors.phone}>
              <div className="flex rounded-md border border-slate-300 bg-white shadow-sm transition focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-100">
                <div className="relative shrink-0 border-r border-slate-200">
                  <Phone className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                  <select
                    name="phoneDialCode"
                    value={formData.phoneDialCode}
                    onChange={handleChange}
                    className="h-full w-32 appearance-none rounded-l-md bg-transparent py-3 pl-9 pr-2 text-sm text-slate-700 outline-none"
                    aria-label="Phone country code"
                  >
                    <option value="">Code</option>
                    {registrationOptions.phoneCountries.map((country) => (
                      <option key={`${country.code}-${country.dialingCode}`} value={country.dialingCode}>
                        {country.code} {country.dialingCode}
                      </option>
                    ))}
                  </select>
                </div>
                <input
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  value={formData.phone}
                  onChange={handleChange}
                  className="min-w-0 flex-1 rounded-r-md bg-transparent px-3 py-3 text-sm text-slate-900 outline-none placeholder:text-slate-400"
                  placeholder="712 345 678"
                  required
                />
              </div>
            </Field>
            <Field label="Password" error={errors.password}>
              <div className="relative">
                <input name="password" type={showPasswords.password ? 'text' : 'password'} value={formData.password} onChange={handleChange} className={`${inputBase} pr-10`} required />
                <button
                  type="button"
                  onClick={() => setShowPasswords((previous) => ({ ...previous, password: !previous.password }))}
                  className="absolute right-3 top-3.5 text-slate-400 transition hover:text-slate-700"
                  aria-label={showPasswords.password ? 'Hide password' : 'Show password'}
                >
                  {showPasswords.password ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
            <Field label="Confirm password" error={errors.confirmPassword}>
              <div className="relative">
                <input name="confirmPassword" type={showPasswords.confirmPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={handleChange} className={`${inputBase} pr-10`} required />
                <button
                  type="button"
                  onClick={() => setShowPasswords((previous) => ({ ...previous, confirmPassword: !previous.confirmPassword }))}
                  className="absolute right-3 top-3.5 text-slate-400 transition hover:text-slate-700"
                  aria-label={showPasswords.confirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showPasswords.confirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </Field>
          </div>

          <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
            <input id="legal-consent" type="checkbox" required className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
            <span className="min-w-0 flex-1 leading-6">
              <label htmlFor="legal-consent" className="cursor-pointer">I agree to the</label>{' '}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
              >
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
              >
                Privacy Policy
              </Link>.
            </span>
          </div>

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => {
                setCurrentStep(1);
                setErrors({});
              }}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-600 px-5 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Creating account...' : 'Create account'}
              {!loading && <Check className="h-4 w-4" />}
            </button>
          </div>
        </form>
      )}

      <div className="mt-6 border-t border-slate-200 pt-5 text-center text-sm text-slate-600">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-sky-700 hover:text-sky-800">
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
}

export default RegisterPage;
